import { drawDecor } from './decor';
import { drawHouse } from './houses';
import type { DecorKind } from './world';
import { speciesCanvas } from '../lib/aquarium';

// ─────────────────────────────────────────────────────────────────────────────
// Inside a fish house: a room the owner decorates (wallpaper, floor and things
// they drag around) where the house's fish swim. Saved as ocean_houses.interior
// (supabase/migrations/20261018_house_interiors.sql) in room units: the room is
// ROOM_W × ROOM_H, and the floor line is at FLOOR_Y.
// ─────────────────────────────────────────────────────────────────────────────

export const ROOM_W = 960;
export const ROOM_H = 600;
export const FLOOR_Y = 500;
export const MAX_ITEMS = 40;

const INK = '#1e2633';

export const WALLS = {
  lagoon: { name: 'Lagoon', top: '#8fe3ff', bottom: '#2b9ed6' },
  deep: { name: 'Deep blue', top: '#3b6fd1', bottom: '#14244f' },
  coral: { name: 'Coral pink', top: '#ffd1dc', bottom: '#f27a98' },
  kelp: { name: 'Kelp green', top: '#b9f0c4', bottom: '#2f8f5b' },
  sunset: { name: 'Sunset', top: '#ffd27a', bottom: '#e8607a' },
  night: { name: 'Glow night', top: '#2a1e5c', bottom: '#0b0b24' },
} as const;
export type WallId = keyof typeof WALLS;

export const FLOORS = {
  sand: { name: 'Sand', color: '#f1d79a', dark: '#d9b871' },
  pebbles: { name: 'Pebbles', color: '#b9b2a6', dark: '#8b857a' },
  planks: { name: 'Planks', color: '#b07a4c', dark: '#7d5232' },
  tiles: { name: 'Tiles', color: '#e9eef5', dark: '#9fb0c6' },
  moss: { name: 'Moss', color: '#7cc37a', dark: '#4d9550' },
} as const;
export type FloorId = keyof typeof FLOORS;

/** What can go in a room. `colors` is how many colour choices it has. */
type ItemDef = { name: string; icon: string; colors: number; scale: number; decor?: DecorKind; r: number; h: number };
export const ITEMS = {
  grass: { name: 'Sea grass', icon: '🌱', colors: 1, scale: 1.3, decor: 'grass', r: 40, h: 80 },
  kelp: { name: 'Kelp', icon: '🌿', colors: 1, scale: 0.42, decor: 'kelp', r: 40, h: 300 },
  branch: { name: 'Branch coral', icon: '🪸', colors: 6, scale: 1.5, decor: 'branch', r: 50, h: 110 },
  fan: { name: 'Sea fan', icon: '🪭', colors: 6, scale: 1.2, decor: 'fan', r: 50, h: 90 },
  brain: { name: 'Brain coral', icon: '🧠', colors: 3, scale: 1.2, decor: 'brain', r: 45, h: 50 },
  anemone: { name: 'Anemone', icon: '🌺', colors: 3, scale: 1.4, decor: 'anemone', r: 45, h: 70 },
  sponge: { name: 'Sponge', icon: '🧽', colors: 6, scale: 1.1, decor: 'sponge', r: 35, h: 90 },
  crystal: { name: 'Crystal', icon: '💎', colors: 3, scale: 1.1, decor: 'crystal', r: 40, h: 100 },
  rock: { name: 'Rock', icon: '🪨', colors: 3, scale: 1.4, decor: 'rock', r: 55, h: 50 },
  shell: { name: 'Shell', icon: '🐚', colors: 3, scale: 1.6, decor: 'shell', r: 25, h: 35 },
  star: { name: 'Starfish', icon: '⭐', colors: 3, scale: 1.4, decor: 'star', r: 25, h: 35 },
  barrel: { name: 'Barrel', icon: '🛢️', colors: 1, scale: 1.2, decor: 'barrel', r: 35, h: 70 },
  anchor: { name: 'Anchor', icon: '⚓', colors: 1, scale: 0.9, decor: 'anchor', r: 50, h: 100 },
  chest: { name: 'Treasure', icon: '🪙', colors: 1, scale: 0.8, r: 45, h: 70 },
  castle: { name: 'Castle', icon: '🏰', colors: 3, scale: 1, r: 70, h: 150 },
  bubbler: { name: 'Bubbler', icon: '🫧', colors: 1, scale: 1, r: 30, h: 60 },
  rug: { name: 'Rug', icon: '🟥', colors: 4, scale: 1, r: 90, h: 20 },
  lamp: { name: 'Lamp', icon: '💡', colors: 3, scale: 1, r: 30, h: 120 },
  painting: { name: 'Painting', icon: '🖼️', colors: 4, scale: 1, r: 55, h: 90 },
  window: { name: 'Round window', icon: '⭕', colors: 1, scale: 1, r: 55, h: 110 },
  // New (2026-10-21, students asked for more to buy).
  otter: { name: 'Otter plushie', icon: '🦦', colors: 1, scale: 1, r: 80, h: 60 },
  trophy: { name: 'Trophy', icon: '🏆', colors: 3, scale: 1, r: 35, h: 90 },
  ship: { name: 'Model ship', icon: '⛵', colors: 3, scale: 1, r: 60, h: 110 },
  clam_bed: { name: 'Clam bed', icon: '🦪', colors: 3, scale: 1, r: 75, h: 70 },
} satisfies Record<string, ItemDef>;
export type ItemId = keyof typeof ITEMS;

/** One placed thing. x, y: where it stands (its base) in room units. */
export type RoomItem = { k: ItemId; x: number; y: number; s: number; c: number; f: boolean; r: number };
export type Interior = { wall: WallId; floor: FloorId; items: RoomItem[] };

export const EMPTY_INTERIOR: Interior = { wall: 'lagoon', floor: 'sand', items: [] };

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** Whatever came from the database, made safe to draw. */
export function cleanInterior(x: unknown): Interior {
  const o = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const wall = (typeof o.wall === 'string' && o.wall in WALLS ? o.wall : 'lagoon') as WallId;
  const floor = (typeof o.floor === 'string' && o.floor in FLOORS ? o.floor : 'sand') as FloorId;
  const items: RoomItem[] = [];
  for (const it of Array.isArray(o.items) ? o.items.slice(0, MAX_ITEMS) : []) {
    const i = (it ?? {}) as Record<string, unknown>;
    if (typeof i.k !== 'string' || !(i.k in ITEMS)) continue;
    const k = i.k as ItemId;
    items.push({
      k,
      x: clamp(Number(i.x) || 0, 0, ROOM_W),
      y: clamp(Number(i.y) || FLOOR_Y, 0, ROOM_H),
      s: clamp(Number(i.s) || 1, 0.5, 2.2),
      c: clamp(Math.round(Number(i.c) || 0), 0, ITEMS[k].colors - 1),
      f: !!i.f,
      r: Math.round(Math.abs(Number(i.r) || 0)) % 100000,
    });
  }
  return { wall, floor, items };
}

/**
 * What saving would cost: each thing added since `before`, counted per kind, at
 * its price. Same rule as ocean_decorate_house (20261019_decor_prices.sql).
 */
export function addedCost(before: Interior, after: Interior, prices: Record<string, number>): number {
  const count = (r: Interior) => r.items.reduce<Record<string, number>>((m, it) => ({ ...m, [it.k]: (m[it.k] ?? 0) + 1 }), {});
  const was = count(before), now = count(after);
  return Object.entries(now).reduce((sum, [k, n]) => sum + Math.max(0, n - (was[k] ?? 0)) * (prices[k] ?? 0), 0);
}

export function newItem(k: ItemId): RoomItem {
  const y = k === 'painting' || k === 'window' ? 230 : k === 'lamp' ? FLOOR_Y + 10 : FLOOR_Y + 20;
  return { k, x: ROOM_W / 2 + (Math.random() - 0.5) * 200, y, s: 1, c: 0, f: false, r: Math.floor(Math.random() * 100000) };
}

/** Is (x, y) on this item? Rough: a box around its base. */
export function hitItem(it: RoomItem, x: number, y: number): boolean {
  const d = ITEMS[it.k], sc = it.s;
  return Math.abs(x - it.x) < d.r * sc && y < it.y + 14 && y > it.y - d.h * sc - 10;
}

/** The empty room: wallpaper, a light from above, and the floor. */
export function drawRoom(g: CanvasRenderingContext2D, wall: WallId, floor: FloorId, t: number) {
  const w = WALLS[wall], f = FLOORS[floor];
  const sky = g.createLinearGradient(0, 0, 0, FLOOR_Y);
  sky.addColorStop(0, w.top); sky.addColorStop(1, w.bottom);
  g.fillStyle = sky; g.fillRect(0, 0, ROOM_W, FLOOR_Y);
  // Light rays.
  g.save();
  g.globalAlpha = wall === 'night' || wall === 'deep' ? 0.06 : 0.14;
  g.fillStyle = '#ffffff';
  for (let i = 0; i < 5; i++) {
    const x = 120 + i * 190 + Math.sin(t * 0.3 + i) * 20;
    g.beginPath(); g.moveTo(x - 30, 0); g.lineTo(x + 30, 0); g.lineTo(x + 120, FLOOR_Y); g.lineTo(x + 20, FLOOR_Y); g.closePath(); g.fill();
  }
  g.restore();
  if (wall === 'night') {
    g.fillStyle = 'rgba(200,230,255,0.7)';
    for (let i = 0; i < 40; i++) {
      const x = (i * 211) % ROOM_W, y = (i * 97) % (FLOOR_Y - 60);
      g.globalAlpha = 0.3 + 0.7 * Math.abs(Math.sin(t * 0.8 + i));
      g.beginPath(); g.arc(x, y, 1.6, 0, Math.PI * 2); g.fill();
    }
    g.globalAlpha = 1;
  }
  // Floor.
  g.fillStyle = f.color; g.fillRect(0, FLOOR_Y, ROOM_W, ROOM_H - FLOOR_Y);
  g.strokeStyle = f.dark; g.fillStyle = f.dark; g.lineWidth = 2;
  if (floor === 'planks') {
    for (let y = FLOOR_Y + 25; y < ROOM_H; y += 25) { g.beginPath(); g.moveTo(0, y); g.lineTo(ROOM_W, y); g.stroke(); }
    for (let row = 0; row * 25 < ROOM_H - FLOOR_Y; row++) for (let x = (row % 2) * 70; x < ROOM_W; x += 140) { g.beginPath(); g.moveTo(x, FLOOR_Y + row * 25); g.lineTo(x, FLOOR_Y + row * 25 + 25); g.stroke(); }
  } else if (floor === 'tiles') {
    for (let y = FLOOR_Y; y < ROOM_H; y += 33) { g.beginPath(); g.moveTo(0, y); g.lineTo(ROOM_W, y); g.stroke(); }
    for (let x = 0; x < ROOM_W; x += 60) { g.beginPath(); g.moveTo(x, FLOOR_Y); g.lineTo(x, ROOM_H); g.stroke(); }
  } else if (floor === 'pebbles') {
    for (let i = 0; i < 90; i++) { g.beginPath(); g.ellipse((i * 137) % ROOM_W, FLOOR_Y + 8 + ((i * 53) % (ROOM_H - FLOOR_Y - 12)), 7 + (i % 4), 5, 0, 0, Math.PI * 2); g.fill(); }
  } else {
    for (let i = 0; i < 60; i++) { g.beginPath(); g.arc((i * 151) % ROOM_W, FLOOR_Y + 6 + ((i * 41) % (ROOM_H - FLOOR_Y - 8)), 1.6, 0, Math.PI * 2); g.fill(); }
  }
  g.strokeStyle = INK; g.lineWidth = 3;
  g.beginPath(); g.moveTo(0, FLOOR_Y); g.lineTo(ROOM_W, FLOOR_Y); g.stroke();
}

const RUGS = ['#e76f51', '#2a9d8f', '#8e7dff', '#f4a261'];
const FRAMES = ['#f7b267', '#7bdff2', '#f25f5c', '#b2f7ef'];
const STONE = ['#c9c3b5', '#f2b5c4', '#a9c8f0'];
const LAMPS = ['#ffe066', '#9ff7ff', '#ffb3e6'];

/** One item, standing at its base. */
export function drawItem(g: CanvasRenderingContext2D, it: RoomItem, t: number, selected = false) {
  const d: ItemDef = ITEMS[it.k];
  const s = it.s * d.scale;
  g.save();
  g.translate(it.x, it.y);
  if (it.f) g.scale(-1, 1);
  g.lineJoin = 'round'; g.lineCap = 'round'; g.strokeStyle = INK; g.lineWidth = 3;
  if (d.decor) {
    drawDecor(g, { kind: d.decor, x: 0, y: 0, s, seed: it.r, color: it.c }, t, []);
  } else switch (it.k) {
    case 'chest': drawHouse(g, 'treasure_chest', 0, 0, t); break;
    case 'castle': {
      g.scale(s, s);
      g.fillStyle = STONE[it.c];
      g.beginPath(); g.rect(-60, -100, 120, 100); g.fill(); g.stroke();
      for (const x of [-70, 30]) { g.beginPath(); g.rect(x, -150, 40, 150); g.fill(); g.stroke(); for (let i = 0; i < 3; i++) { g.beginPath(); g.rect(x + i * 14, -162, 10, 12); g.fill(); g.stroke(); } }
      g.fillStyle = '#3d2b4f';
      g.beginPath(); g.moveTo(-18, 0); g.lineTo(-18, -40); g.arc(0, -40, 18, Math.PI, 0); g.lineTo(18, 0); g.closePath(); g.fill(); g.stroke();
      for (const x of [-56, 44]) { g.beginPath(); g.roundRect(x, -125, 12, 18, 6); g.fill(); g.stroke(); }
      break;
    }
    case 'bubbler': {
      g.scale(s, s);
      g.fillStyle = '#6c7a89';
      g.beginPath(); g.roundRect(-22, -26, 44, 30, 8); g.fill(); g.stroke();
      g.fillStyle = '#ffd23d'; g.beginPath(); g.arc(0, -26, 7, Math.PI, 0); g.fill(); g.stroke();
      g.fillStyle = 'rgba(255,255,255,0.75)'; g.strokeStyle = 'rgba(255,255,255,0.9)'; g.lineWidth = 1.5;
      for (let i = 0; i < 8; i++) {
        const p = (t * 0.35 + i / 8) % 1;
        g.globalAlpha = 1 - p;
        g.beginPath(); g.arc(Math.sin(p * 10 + i) * 10, -34 - p * 420, 3 + (i % 3) * 2, 0, Math.PI * 2); g.stroke();
      }
      g.globalAlpha = 1;
      break;
    }
    case 'rug': {
      g.scale(s, s);
      g.fillStyle = RUGS[it.c];
      g.beginPath(); g.ellipse(0, 0, 95, 18, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      g.strokeStyle = 'rgba(255,255,255,0.6)'; g.lineWidth = 3;
      g.beginPath(); g.ellipse(0, 0, 70, 11, 0, 0, Math.PI * 2); g.stroke();
      break;
    }
    case 'lamp': {
      g.scale(s, s);
      const glow = 0.75 + 0.25 * Math.sin(t * 2);
      const halo = g.createRadialGradient(0, -110, 4, 0, -110, 90);
      halo.addColorStop(0, LAMPS[it.c] + 'aa'); halo.addColorStop(1, LAMPS[it.c] + '00');
      g.globalAlpha = glow; g.fillStyle = halo; g.beginPath(); g.arc(0, -110, 90, 0, Math.PI * 2); g.fill(); g.globalAlpha = 1;
      g.strokeStyle = INK; g.lineWidth = 6; g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -95); g.stroke();
      g.lineWidth = 3; g.fillStyle = '#4b3a2a'; g.beginPath(); g.ellipse(0, 0, 24, 6, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      g.fillStyle = LAMPS[it.c]; g.beginPath(); g.arc(0, -110, 20, 0, Math.PI * 2); g.fill(); g.stroke();
      break;
    }
    case 'otter': {
      // The sea otter's own drawing, as a cuddly toy lying on the floor.
      g.scale(s, s);
      const art = speciesCanvas('sea-otter'), w = 170, h = (art.height / art.width) * w;
      g.save(); g.scale(-1, 1); g.drawImage(art, -w / 2, -h, w, h); g.restore();
      break;
    }
    case 'trophy': {
      g.scale(s, s);
      const metal = ['#ffd23d', '#c9d2de', '#d9a066'][it.c];
      g.fillStyle = '#5a3a24'; g.beginPath(); g.roundRect(-28, -16, 56, 16, 3); g.fill(); g.stroke();
      g.fillStyle = metal;
      g.beginPath(); g.roundRect(-8, -36, 16, 20, 2); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(-30, -88); g.lineTo(30, -88); g.quadraticCurveTo(28, -40, 0, -36); g.quadraticCurveTo(-28, -40, -30, -88); g.fill(); g.stroke();
      for (const sd of [-1, 1]) { g.beginPath(); g.arc(sd * 32, -72, 12, sd > 0 ? -Math.PI / 2 : Math.PI / 2, sd > 0 ? Math.PI / 2 : Math.PI * 1.5, sd < 0); g.lineWidth = 5; g.strokeStyle = metal; g.stroke(); g.lineWidth = 3; g.strokeStyle = INK; }
      g.fillStyle = 'rgba(255,255,255,0.6)'; g.fillRect(-18, -82, 6, 30);
      break;
    }
    case 'ship': {
      g.scale(s, s);
      const sail = ['#ffffff', '#ff6b4a', '#3db8ff'][it.c];
      g.fillStyle = '#4b3a2a'; g.beginPath(); g.roundRect(-30, -10, 60, 10, 3); g.fill(); g.stroke();
      g.fillStyle = '#8a5a3b';
      g.beginPath(); g.moveTo(-56, -40); g.lineTo(56, -40); g.lineTo(40, -14); g.lineTo(-40, -14); g.closePath(); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(0, -40); g.lineTo(0, -108); g.lineWidth = 4; g.stroke(); g.lineWidth = 3;
      g.fillStyle = sail;
      g.beginPath(); g.moveTo(4, -104); g.quadraticCurveTo(40, -80, 4, -50); g.closePath(); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(-4, -96); g.quadraticCurveTo(-30, -76, -4, -54); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#ff3b3b'; g.beginPath(); g.moveTo(0, -108); g.lineTo(16, -102); g.lineTo(0, -96); g.closePath(); g.fill();
      break;
    }
    case 'clam_bed': {
      g.scale(s, s);
      const shell = ['#ffb3c7', '#bfe6ff', '#f6e6d2'][it.c];
      g.fillStyle = shell;
      g.beginPath(); g.moveTo(-72, -12); g.quadraticCurveTo(0, 14, 72, -12); g.quadraticCurveTo(0, -4, -72, -12); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(-72, -14); g.quadraticCurveTo(-60, -70, 0, -72); g.quadraticCurveTo(60, -70, 72, -14); g.quadraticCurveTo(0, -24, -72, -14); g.fill(); g.stroke();
      g.strokeStyle = 'rgba(30,38,51,0.35)';
      for (let k = -2; k <= 2; k++) { g.beginPath(); g.moveTo(k * 8, -22); g.lineTo(k * 26, -66); g.stroke(); }
      g.strokeStyle = INK;
      g.fillStyle = '#fff'; g.beginPath(); g.ellipse(-30, -12, 22, 9, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      g.fillStyle = '#f4f4f4'; g.beginPath(); g.arc(26, -20, 11, 0, Math.PI * 2); g.fill(); g.stroke();
      break;
    }
    case 'painting': {
      g.scale(s, s);
      g.fillStyle = FRAMES[it.c];
      g.beginPath(); g.roundRect(-58, -90, 116, 90, 6); g.fill(); g.stroke();
      g.fillStyle = '#bfe9ff'; g.fillRect(-46, -78, 92, 66);
      g.fillStyle = '#ffd23d'; g.beginPath(); g.arc(26, -60, 9, 0, Math.PI * 2); g.fill();
      g.fillStyle = '#ff8a5c';
      g.beginPath(); g.ellipse(-8, -38, 22, 12, 0, 0, Math.PI * 2); g.fill();
      g.beginPath(); g.moveTo(12, -38); g.lineTo(26, -48); g.lineTo(26, -28); g.closePath(); g.fill();
      g.strokeRect(-46, -78, 92, 66);
      break;
    }
    case 'window': {
      g.scale(s, s);
      g.fillStyle = '#9aa6b2'; g.beginPath(); g.arc(0, -55, 55, 0, Math.PI * 2); g.fill(); g.stroke();
      const sea = g.createLinearGradient(0, -100, 0, -10);
      sea.addColorStop(0, '#1f78b4'); sea.addColorStop(1, '#0b3d6b');
      g.fillStyle = sea; g.beginPath(); g.arc(0, -55, 42, 0, Math.PI * 2); g.fill(); g.stroke();
      g.fillStyle = 'rgba(255,255,255,0.5)';
      const p = (t * 0.25) % 1;
      g.beginPath(); g.arc(-10 + Math.sin(p * 8) * 6, -20 - p * 70, 4, 0, Math.PI * 2); g.fill();
      g.fillStyle = '#5a6672';
      for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; g.beginPath(); g.arc(Math.cos(a) * 49, -55 + Math.sin(a) * 49, 3.5, 0, Math.PI * 2); g.fill(); }
      break;
    }
  }
  g.restore();
  if (selected) {
    const r = d.r * it.s, h = d.h * it.s;
    g.save();
    g.setLineDash([8, 6]); g.lineWidth = 2.5; g.strokeStyle = '#ffffff';
    g.strokeRect(it.x - r, it.y - h - 10, r * 2, h + 24);
    g.strokeStyle = INK; g.lineDashOffset = 7; g.strokeRect(it.x - r, it.y - h - 10, r * 2, h + 24);
    g.restore();
  }
}

/** Things on the wall and rugs are drawn first, then the rest by depth (lower = in front). */
export function drawOrder(items: RoomItem[]): RoomItem[] {
  const back = (it: RoomItem) => (it.k === 'painting' || it.k === 'window' ? 0 : it.k === 'rug' ? 1 : 2);
  return [...items].sort((a, b) => back(a) - back(b) || (back(a) === 2 ? a.y - b.y : 0));
}

// ── Fish coming and going ───────────────────────────────────────────────────
// A house's fish spend part of the time swimming around outside it and part
// inside the room, each on its own cycle. It runs on the wall clock, so every
// screen (the ocean outside and the room inside) agrees on where a fish is.

const GO = 2.2;   // seconds to swim through the door

function fishHash(id: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 100000) / 100000;
}

/**
 * Where a fish is now: inside or out, and `door`, how close it is to the doorway
 * (0 = off swimming, 1 = in the doorway) while it goes in or comes out.
 */
export function fishWhere(id: string, now = Date.now() / 1000): { inside: boolean; door: number } {
  const cycle = 40 + fishHash(id, 3) * 40;
  const u = (now + fishHash(id, 7) * cycle) % cycle, half = cycle / 2;
  const inside = u >= half;
  const v = inside ? u - half : u;                 // seconds into this half
  const door = v < GO ? 1 - v / GO : v > half - GO ? (v - (half - GO)) / GO : 0;
  return { inside, door: door * door * (3 - 2 * door) };
}
